import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  FormHelperText,
  Typography,
  Link,
  TextField,
} from "@mui/material";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { LIST_CATEGORIES, LIST_TYPES } from "@/modules/common/constants";
import { defaultUserStatuses } from "@/modules/common/constants";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { muiTheme } from "@/styles/theme";
import {
  UPLOAD_LEADS_TEMPLATE_LINK,
  ACCEPTED_LEAD_FILE_TYPES,
} from "../consts";
import { uploadLeadsListV2 } from "../api/index.v2";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import { useState, useEffect } from "react";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import useAuth from "@/modules/common/hooks/auth.hook";
import { useFetchUserByUId } from "@/modules/user/hooks/use-fetch-user-by-id.hook";
import useUsersFilterOptions from "@/modules/common/hooks/lookups/users-filter-options.hook";
import useLeadTypesFilterOptions from "@/modules/common/hooks/lookups/lead-types-filter-options.hook";

interface UploadLeadsInterface {
  setToggleUploadLeadsModal: (toggleUploadLeadsModal: boolean) => void;
}

const UploadLeads = ({ setToggleUploadLeadsModal }: UploadLeadsInterface) => {
  const [organizationUId, setOrganizationUId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    selectedOrganization,
    filteredManagedOrganizations,
    renderOrganisationsActions,
  } = useOrganization();

  const { user } = useAuth();

  const { userData } = useFetchUserByUId(user?.["cognito:username"], true);

  const {
    usersFilterOptions,
    usersFilterOptionsLoading,
    searchTerm,
    setSearchTerm,
  } = useUsersFilterOptions({
    initialParams: {
      statuses: defaultUserStatuses,
      organizationUIds: organizationUId,
      excludeUsersWithBlockAllLeads: true,
    },
    queryKey: "usersFilterOptions",
  });

  const { leadTypesFilterOptions, setSearchTerm: setSearchTermLeadTypes } =
    useLeadTypesFilterOptions(
      {
        initialParams: {
          organizationUIds: organizationUId,
        },
      },
      {
        allowAllFunnels: true,
      }
    );

  const uploadLeadsSchema = z.object({
    list: z
      .any()
      .refine(
        (list) => ACCEPTED_LEAD_FILE_TYPES.includes(list?.type),
        "Only .xlsx file formats are allowed."
      ),
    leadTypeUId: z.string().min(1, "Lead type is required"),
    organizationUId: z.string().min(1, "Organization is required"),
    userUId: z.string().min(1, "List owner is required"),
    listType: z.string().min(1, "List type is required"),
    listCategory: z.string().min(1, "List category is required"),
    listName: z.string().min(1, "List name is required"),
  });

  type UploadLeadsFormData = z.infer<typeof uploadLeadsSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UploadLeadsFormData>({
    resolver: zodResolver(uploadLeadsSchema),
    defaultValues: {
      list: null,
      leadTypeUId: "",
      organizationUId: !renderOrganisationsActions
        ? selectedOrganization?.uid
        : organizationUId,
      userUId: "",
      listType: "",
      listCategory: "",
      listName: "",
    },
  });

  useEffect(() => {
    if (userData?.data?.uid) {
      setValue("userUId", userData.data.uid, { shouldValidate: true });
    }
  }, [userData?.data?.uid, setValue]);

  const onSubmit = (data: UploadLeadsFormData) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    uploadLeadsMutation.mutate(formData);
  };

  const handleFiles = (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files];
    const file = fileArray[0];
    setValue("list", file, { shouldValidate: true });
  };

  const handleDeleteFile = () => {
    setValue("list", null);
  };

  const uploadLeadsMutation = useMutation({
    mutationFn: (formData: FormData) => uploadLeadsListV2(formData),
    onSuccess: () => {
      setIsSubmitting(false);
      showToast({
        type: ToastType.SUCCESS,
        message:
          "Your file will be processed. You will receive an email shortly",
        toastId: TOAST_IDS.UPLOAD_LEADS_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      setToggleUploadLeadsModal(false);
    },
    onError: (error: {
      response: {
        data: {
          message: string;
        };
      };
    }) => {
      setIsSubmitting(false);
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ||
          "Unexpected error occurred. Please try again.",
        toastId: TOAST_IDS.UPLOAD_LEADS_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={2}>
        <FormControl fullWidth>
          <Box width="100%">
            <FileUploadButton
              {...register("list")}
              setFiles={handleFiles}
              acceptedFileTypes={ACCEPTED_LEAD_FILE_TYPES}
              maxFiles={1}
              onDeleteFile={handleDeleteFile}
            />
            {!watch("list") && isSubmitting && (
              <Typography variant="caption" color="error">
                File is required
              </Typography>
            )}
            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="bold"
              >
                Please use our
              </Typography>
              <Link
                href={UPLOAD_LEADS_TEMPLATE_LINK}
                target="_blank"
                rel="noopener"
                sx={{
                  color: muiTheme.colorSchemes.light.palette.primary[500],
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                template file
              </Link>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="bold"
              >
                for uploading leads.
              </Typography>
            </Box>
          </Box>
        </FormControl>
        {renderOrganisationsActions && (
          <Box flex={1}>
            <FormControl fullWidth>
              <SingleAutoCompleteSelectChip
                name="organizationUId"
                options={
                  filteredManagedOrganizations?.map((type) => ({
                    value: type?.organization?.uid as string,
                    label: type?.organization?.name as string,
                  })) || []
                }
                value={watch("organizationUId")}
                label="Organization"
                error={errors.organizationUId?.message}
                onChange={(value) => {
                  setValue("organizationUId", value.toString());
                  setOrganizationUId(value.toString());
                }}
              />
            </FormControl>
          </Box>
        )}
        <FormControl fullWidth>
          <SingleSelectSearch
            options={[
              ...(usersFilterOptions?.map((user) => ({
                value: user.uid,
                label: user.name,
                color: user.color,
              })) || []),
              ...(userData
                ? [
                    {
                      value: userData.data.uid,
                      label: userData.data.name,
                      color: userData.data.color,
                    },
                  ]
                : []),
            ]}
            disabled={renderOrganisationsActions && !organizationUId}
            defaultValue={userData?.data.uid}
            isLoading={usersFilterOptionsLoading}
            label="List Owner"
            error={errors.userUId?.message}
            onValueChange={(value) => {
              setValue("userUId", value || "");
            }}
            searchValue={searchTerm}
            onSearchValueChange={setSearchTerm}
            size="medium"
          />{" "}
        </FormControl>
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
          <Box flex={1}>
            <FormControl fullWidth>
              <TextField
                {...register("listName")}
                label="List Name"
                error={!!errors.listName}
                fullWidth
              />
              {errors.listName && (
                <FormHelperText error={true}>
                  {errors.listName.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box flex={1}>
            <FormControl fullWidth>
              <SingleAutoCompleteSelectChip
                name="leadTypeUId"
                options={
                  leadTypesFilterOptions
                    ?.map((type) => ({
                      value: type.uid,
                      label: type.name,
                    }))
                    .filter((leadType) => leadType.value !== undefined) || []
                }
                disabled={renderOrganisationsActions && !organizationUId}
                value={watch("leadTypeUId")}
                label="Lead Type"
                error={errors.leadTypeUId?.message}
                onChange={(value) => setValue("leadTypeUId", value.toString())}
                onInputChange={(value) => setSearchTermLeadTypes(value)}
              />
            </FormControl>
          </Box>
        </Box>

        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
          <Box flex={1}>
            <FormControl fullWidth>
              <InputLabel>List Type</InputLabel>
              <Select
                {...register("listType")}
                label="List Type"
                error={!!errors.listType}
              >
                {LIST_TYPES.map((listType) => (
                  <MenuItem value={listType.value}>{listType.label}</MenuItem>
                ))}
              </Select>
              {errors.listType && (
                <FormHelperText error={true}>
                  {errors.listType.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box flex={1}>
            <FormControl fullWidth>
              <InputLabel>List Category</InputLabel>
              <Select
                {...register("listCategory")}
                label="List Category"
                error={!!errors.listCategory}
              >
                {LIST_CATEGORIES.map((listCategory) => (
                  <MenuItem value={listCategory.value}>
                    {listCategory.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.listCategory && (
                <FormHelperText error={true}>
                  {errors.listCategory.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            onClick={() => setToggleUploadLeadsModal(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              minWidth: "80px",
            }}
            variant="contained"
            type="submit"
            // disabled={uploadLeadsMutation.isLoading || !watch("list")}
            onClick={() => setIsSubmitting(true)}
          >
            {uploadLeadsMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              "Upload"
            )}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default UploadLeads;
